import { parseExploreAddress } from "../analytics/explore/explore-address";
import { METRIC_INFO } from "../analytics/explore/metric-catalog";
import type { Crumb } from "../ui/breadcrumbs";
import { areaForPath, isAreaOpenable, isUnlistedAreaPath, type Offering } from "./offering";

// The trail (breadcrumbs) and the page title of a route. They come from the address only and use fixed words: never a customer name, a
// request title or any other text from the API. (A page that knows a safe, better title sets it with `useDocumentTitle`.)

const AREA_LABELS: Readonly<Record<string, string>> = {
  home: "Home",
  concierge: "Concierge",
  customers: "Customers",
  privacy: "Privacy",
  requests: "Requests",
  coverage: "Coverage",
  trips: "Trips",
  itinerary: "Itinerary",
  actions: "Actions",
  payments: "Payments",
  catalog: "Catalog",
  search: "Search",
  places: "Places",
  review: "Review",
  new: "New",
  imports: "Imports",
  analytics: "Analytics",
  operations: "Operations",
  diagnostics: "Diagnostics",
  audit: "Audit trail",
  runbooks: "Runbooks",
  // The trail already starts with "Staff" (the console), so the page of the team is "Team and access", not "Staff / Staff".
  staff: "Team and access",
  memory: "Memory",
  login: "Sign in",
};

// What an identifier is, by the segment before it.
const ENTITY_LABELS: Readonly<Record<string, string>> = {
  customers: "Customer",
  analytics: "Customer",
  requests: "Request",
  payments: "Payment",
  trips: "Trip",
  itinerary: "Plan",
  imports: "Import",
  items: "Item",
  concierge: "Conversation",
  runbooks: "Runbook",
  facts: "Memory fact",
};

// Segments that only group (they have no page and no crumb of their own).
const GROUP_ONLY = new Set(["items", "facts"]);

// Paths that have a page of their own, so a crumb may link to them. Anything else is plain text.
const LINKABLE: readonly RegExp[] = [
  /^\/[a-z-]+$/,
  /^\/customers\/[^/]+$/,
  /^\/analytics\/[^/]+$/,
  /^\/customers\/[^/]+\/memory$/,
  /^\/requests\/[^/]+$/,
  /^\/payments\/[^/]+$/,
  /^\/imports\/[^/]+$/,
  /^\/catalog\/items\/[^/]+$/,
  /^\/operations\/(diagnostics|audit|runbooks)$/,
  /^\/coverage\/customers\/[^/]+$/,
];

/** The first crumb of every trail: the console itself. It links to Home, the page every staff member has. */
const STAFF_ROOT: Crumb = { label: "Staff", to: "/home" };
const ROOT_FALLBACK: Crumb = { label: "Home", to: "/home" };

type Step = Readonly<{ label: string; path: string }>;

/** A segment as the router reads it: `%`-escapes decoded, case ignored. A segment that cannot be decoded is kept as it is. */
function readSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    return segment.toLowerCase();
  }
}

/** The words of the trail of an address, each with the path it stands for. */
function trailOf(pathname: string): readonly Step[] {
  const segments = pathname.split("/").filter(Boolean);
  const words = segments.map(readSegment);
  const steps: Step[] = [];
  let path = "";
  for (const [index, segment] of segments.entries()) {
    // The path keeps the segment as written (an identifier may be case-sensitive); only the word is looked up the router's way.
    path += `/${segment}`;
    const word = words[index] as string;
    if (GROUP_ONLY.has(word)) continue;
    const label =
      AREA_LABELS[word] ??
      (index > 0 ? ENTITY_LABELS[words[index - 1] ?? ""] : undefined) ??
      "Details";
    steps.push({ label, path });
  }
  return steps;
}

/**
 * The trail of an address. It starts with "Staff" on every page (the console), then the words of the address. A crumb links to a page that has one, in an area that opens for this staff member (`offering`): a crumb of an
 * area the role cannot open (the administrator on `/customers/privacy` sees "Customers") is plain text, never a link into a refusal.
 */
export function routeCrumbs(pathname: string, offering: Offering, search = ""): readonly Crumb[] {
  const steps = trailOf(pathname);
  if (steps.length === 0) return [STAFF_ROOT, ROOT_FALLBACK];
  const trail = steps.map(({ label, path }) => {
    const area = areaForPath(path);
    const linkable =
      LINKABLE.some((pattern) => pattern.test(path.toLowerCase())) &&
      !isUnlistedAreaPath(path.toLowerCase()) &&
      area !== undefined &&
      isAreaOpenable(area.key, offering);
    return linkable ? { label, to: path } : { label };
  });
  return [STAFF_ROOT, ...trail, ...exploreCrumb(pathname, search)];
}

const CUSTOMER_COCKPIT = /^\/analytics\/[^/]+\/?$/;

/**
 * The Explore tab of a customer adds the metric to the trail: "Staff / Analytics / Customer / Sessions". The metric comes from a CLOSED list of fixed
 * words (`metric-catalog.ts`), never from the API, so the rule that a crumb holds no customer text stays true.
 */
function exploreCrumb(pathname: string, search: string): readonly Crumb[] {
  if (!CUSTOMER_COCKPIT.test(pathname.toLowerCase())) return [];
  const params = new URLSearchParams(search);
  if (params.get("tab") !== "explore") return [];
  return [{ label: METRIC_INFO[parseExploreAddress(params).metric].label }];
}

// Pages whose last crumb is a word that two areas share ("New", "Review", "Customer"). The trail keeps the short word (it sits under the
// name of its area); the tab title is longer, so a person with many tabs can tell the pages apart (WCAG 2.4.2).
const TITLE_OVERRIDES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/staff$/, "Staff"],
  [/^\/analytics\/[^/]+$/, "Customer analytics"],
  [/^\/coverage\/customers\/[^/]+$/, "Customer coverage"],
  [/^\/catalog\/new$/, "New catalog item"],
  [/^\/imports\/new$/, "New import"],
  [/^\/catalog\/places\/review$/, "Place review"],
  [/^\/imports\/[^/]+\/review$/, "Import review"],
];

/** The title of the page for the browser tab: the last crumb (or a longer fixed title above), as `<Page> · Lafayette Staff`. */
export function routeTitle(pathname: string): string {
  const override = TITLE_OVERRIDES.find(([pattern]) => pattern.test(pathname));
  if (override !== undefined) return override[1];
  return trailOf(pathname).at(-1)?.label ?? "Home";
}
